// src/components/notifications/NotificationDropdown.jsx
//
// Panel that opens beneath the bell in TopToolbar. Shows the last 10
// notifications grouped by read state, with type-based icons and
// per-item navigation. Used by the existing NotificationsBell —
// preserved its props: onClose() and onCountChange(remaining).

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, Zap, FileText, Ticket, Bell as BellIcon,
  AlertTriangle, MessageSquare, ArrowRight,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Visual constants
const NEON       = "#00FF99";
const PANEL_BG   = "#0a0a0a";
const PANEL_BORD = "rgba(0,255,153,0.25)";
const MUTED      = "#666";
const TEXT       = "#fff";
const TIME_MUTED = "#444";

// Map a notification type → (icon, accent colour)
function iconForType(t) {
  const k = (t || "").toLowerCase();
  if (k.includes("approv") || k === "leave_approved" || k === "success")  return { I: Check,         tint: "#22c55e" };
  if (k.includes("kira") || k === "action")                                return { I: Zap,           tint: NEON       };
  if (k.includes("polic"))                                                 return { I: FileText,      tint: "#a78bfa" };
  if (k.includes("ticket"))                                                return { I: Ticket,        tint: "#fb923c" };
  if (k.includes("warn"))                                                  return { I: AlertTriangle, tint: "#f59e0b" };
  if (k.includes("dm") || k.includes("message"))                           return { I: MessageSquare, tint: "#60a5fa" };
  return { I: BellIcon, tint: MUTED };
}

// Safe internal-only route resolver
function getNotifRoute(n) {
  const link = n?.link;
  if (typeof link !== "string" || !link.startsWith("/")) return null;
  return link;
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const now  = new Date();
  const diff = (now - then) / 1000;
  if (diff <  60)               return "just now";
  if (diff <  60 * 60)          return `${Math.floor(diff /   60)}m ago`;
  if (diff <  60 * 60 * 24)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff <  60 * 60 * 24 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

// ─────────────────────────────────────────────────────────
//  Main
// ─────────────────────────────────────────────────────────
export default function NotificationDropdown({ onClose, onCountChange }) {
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const navigate = useNavigate();

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    const tok = localStorage.getItem("token");
    if (!tok) { setLoading(false); return; }
    fetch(`${API_BASE}/api/notifications?limit=10`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        if (cancelled) return;
        const list = Array.isArray(d) ? d : (d?.items || d?.notifications || d?.data || []);
        setNotifs(list.slice(0, 10));
      })
      .catch(e => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const markAllRead = async (e) => {
    e?.stopPropagation();
    const tok = localStorage.getItem("token");
    if (!tok) return;
    try {
      await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}` },
      });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
      onCountChange?.(0);
    } catch {
      // Silent — mark-read is a routine background action; we don't toast on
      // failure. Catch is intentionally empty so the click handler can't crash.
    }
  };

  const handleItemClick = async (n) => {
    const tok = localStorage.getItem("token");
    if (tok && !n.is_read) {
      try {
        await fetch(`${API_BASE}/api/notifications/${n.id}/read`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${tok}` },
        });
      } catch {
        // Silent — routine mark-as-read; don't surface a toast. Empty catch
        // is intentional to keep the navigation flow alive.
      }
    }
    if (!n.is_read) {
      const remaining = notifs.filter(x => !x.is_read && x.id !== n.id).length;
      onCountChange?.(remaining);
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
    }
    const route = getNotifRoute(n);
    if (route) navigate(route);
    onClose?.();
  };

  // ── Render ──
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position:     "absolute",
        top:          "100%",
        right:        0,
        marginTop:    8,
        width:        320,
        maxHeight:    520,
        display:      "flex",
        flexDirection:"column",
        background:   PANEL_BG,
        border:       `0.5px solid ${PANEL_BORD}`,
        borderRadius: 12,
        boxShadow:    "0 10px 30px rgba(0,0,0,0.6)",
        zIndex:       1000,
        color:        TEXT,
      }}
    >
      {/* Header */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "12px 16px",
        borderBottom:   "0.5px solid #1a1a1a",
        flexShrink:     0,
      }}>
        <span style={{ color: TEXT, fontWeight: 500, fontSize: 14 }}>
          Notifications
        </span>
        <button
          type="button"
          onClick={markAllRead}
          style={{
            background: "transparent",
            border:     "none",
            color:      NEON,
            fontSize:   12,
            cursor:     "pointer",
            padding:    0,
          }}
        >
          Mark all read
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <SkeletonList />
        ) : error ? (
          <div style={{ padding: 24, textAlign: "center", color: "#E24B4A", fontSize: 13 }}>
            Couldn't load notifications.
          </div>
        ) : notifs.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: MUTED, fontSize: 13 }}>
            No notifications yet
          </div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {notifs.map((n) => {
              const { I, tint } = iconForType(n.type);
              return (
                <li
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  style={{
                    cursor:        "pointer",
                    padding:       "12px 16px",
                    borderLeft:    n.is_read ? "2px solid transparent" : `2px solid ${NEON}`,
                    borderBottom:  "0.5px solid #1a1a1a",
                    opacity:       n.is_read ? 0.6 : 1,
                    display:       "flex",
                    gap:           10,
                    transition:    "background 0.12s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#0d0d0d"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: "#0d0d0d",
                    border: `0.5px solid ${tint}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: tint, flexShrink: 0, marginTop: 2,
                  }}>
                    <I size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {n.title && (
                      <div style={{
                        fontSize: 13, color: TEXT,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>{n.title}</div>
                    )}
                    {n.body && (
                      <div style={{
                        fontSize: 12, color: MUTED, marginTop: 2,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>{n.body}</div>
                    )}
                    <div style={{ fontSize: 11, color: TIME_MUTED, marginTop: 4 }}>
                      {relativeTime(n.created_at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <button
        type="button"
        onClick={() => { onClose?.(); navigate("/messenger/notifications"); }}
        style={{
          padding: "12px 16px",
          background: "transparent",
          border: "none",
          borderTop: "0.5px solid #1a1a1a",
          color: NEON, fontSize: 12, cursor: "pointer",
          display: "flex", justifyContent: "center", alignItems: "center", gap: 6,
          flexShrink: 0,
        }}
      >
        View all notifications <ArrowRight size={12} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Skeleton
// ─────────────────────────────────────────────────────────
function SkeletonList() {
  return (
    <>
      <style>{`
        @keyframes kwery-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
      `}</style>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{
          padding: "12px 16px",
          display: "flex", gap: 10,
          borderBottom: "0.5px solid #1a1a1a",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "#1a1a1a",
            animation: "kwery-shimmer 1.5s linear infinite",
            backgroundImage: "linear-gradient(90deg,#111 25%,#1a1a1a 50%,#111 75%)",
            backgroundSize: "200% 100%", flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 12, width: "70%", background: "#1a1a1a",
                            borderRadius: 4, marginBottom: 6,
                            backgroundImage: "linear-gradient(90deg,#111 25%,#1a1a1a 50%,#111 75%)",
                            backgroundSize: "200% 100%",
                            animation: "kwery-shimmer 1.5s linear infinite" }} />
            <div style={{ height: 10, width: "50%", background: "#1a1a1a",
                            borderRadius: 4,
                            backgroundImage: "linear-gradient(90deg,#111 25%,#1a1a1a 50%,#111 75%)",
                            backgroundSize: "200% 100%",
                            animation: "kwery-shimmer 1.5s linear infinite" }} />
          </div>
        </div>
      ))}
    </>
  );
}
